import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { downscaleImage } from '../../utils/imageDownscale';
import { realtime } from '../../utils/realtime';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Send, Image as ImageIcon, Loader2, MapPin, Calendar,
  Users, X, Paperclip, CheckCheck, Trash2, Wifi, WifiOff, UserPlus, Siren,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTalentProfileModal } from '../../components/TalentProfileModalProvider';
import { formatDateMonthDay, formatTime12h } from '../../utils/timeFormat';
import ChannelPinTray, { CHANNEL_TYPE_META } from '../../components/ChannelPinTray';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';

/**
 * Iter-181 Messaging Slice 3 — Event Channel Detail with realtime.
 *
 * Lives over WebSocket (typing/presence/messages); polls every 12s as a
 * safety net while the socket reconnects. Per-message delete for sender
 * + admins/clients/shift-leads.
 */
const EventChannelDetail = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { open: openProfile } = useTalentProfileModal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]); // local URLs
  const [uploading, setUploading] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // user_id -> last_ts
  const [onlineUsers, setOnlineUsers] = useState({}); // user_id -> bool
  const [wsConnected, setWsConnected] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addQ, setAddQ] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const typingDebounceRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const load = useCallback(async (mark = true) => {
    try {
      const res = await api.get(`/messages/channels/${channelId}`);
      setData(res.data);
      if (mark) {
        try { await api.post(`/messages/channels/${channelId}/read`); } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load channel');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    load(true);
    // Iter-181 — connect realtime + subscribe to channel.
    const token = localStorage.getItem('token');
    if (token) {
      realtime.connect(token);
      realtime.subscribe(channelId);
    }

    const off = realtime.on((event) => {
      if (event.type === 'connected') setWsConnected(true);
      else if (event.type === 'disconnected') setWsConnected(false);
      else if (event.type === 'subscribed') { /* ack */ }
      else if (event.type === 'message.new' && event.channel_id === channelId) {
        setData(prev => prev ? { ...prev, messages: [...(prev.messages || []), {
          id: event.data.id,
          sender_id: event.data.sender_id,
          sender_name: event.data.sender_name,
          sender_photo_url: event.data.sender_photo_url || null,
          is_ai: !!event.data.is_ai,
          content: event.data.content,
          attachments: event.data.attachments || [],
          is_system: !!event.data.is_system,
          is_deleted: false,
          created_at: event.data.created_at,
          seen_by: [],
          seen_by_count: 0,
        }] } : prev);
        // Auto-mark read since user has the channel open.
        realtime.sendRead(channelId);
      } else if (event.type === 'message.deleted' && event.channel_id === channelId) {
        setData(prev => prev ? {
          ...prev,
          messages: (prev.messages || []).map(m => m.id === event.data.message_id
            ? { ...m, is_deleted: true, content: '', attachments: [], deleted_reason: event.data.reason }
            : m),
        } : prev);
      } else if ((event.type === 'typing.start' || event.type === 'typing.stop') && event.channel_id === channelId) {
        setTypingUsers(prev => {
          const next = { ...prev };
          if (event.type === 'typing.start') next[event.user_id] = Date.now();
          else delete next[event.user_id];
          return next;
        });
      } else if (event.type === 'presence.online') {
        setOnlineUsers(prev => ({ ...prev, [event.user_id]: true }));
      } else if (event.type === 'presence.offline') {
        setOnlineUsers(prev => ({ ...prev, [event.user_id]: false }));
      } else if (event.type === 'read.update' && event.channel_id === channelId) {
        // Refetch to get latest seen_by counts.
        load(false);
      }
    });

    // Polling fallback (slower than before — 12s — since WS handles real-time).
    pollRef.current = setInterval(() => {
      if (!realtime.connected) load(false);
    }, 12000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      realtime.unsubscribe(channelId);
      off();
    };
  }, [channelId, load]);

  // Stale typing-indicator garbage collect (drop after 5s with no refresh).
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => {
        const next = {};
        Object.entries(prev).forEach(([uid, ts]) => { if (now - ts < 5000) next[uid] = ts; });
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages?.length]);

  const sendTypingThrottled = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000) {
      realtime.sendTyping(channelId, true);
      lastTypingSentRef.current = now;
    }
    if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    typingDebounceRef.current = setTimeout(() => {
      realtime.sendTyping(channelId, false);
      lastTypingSentRef.current = 0;
    }, 3000);
  }, [channelId]);

  const send = async () => {
    if ((!content.trim() && attachments.length === 0) || sending) return;
    setSending(true);
    const sentContent = content.trim() || (attachments.length ? '[image]' : '');
    const sentAttachments = attachments;
    try {
      const resp = await api.post(`/messages/channels/${channelId}/messages`, {
        content: sentContent,
        attachments: sentAttachments,
        urgent,
      });
      const newId = resp?.data?.id;
      setContent('');
      setAttachments([]);
      setUrgent(false);
      realtime.sendTyping(channelId, false);
      lastTypingSentRef.current = 0;
      // Optimistically show the sender's OWN message right away — the realtime
      // fan-out excludes the sender, so without this it only appeared after a
      // manual page reload. De-duped by id if the server copy arrives later.
      if (newId) {
        setData(prev => {
          if (!prev) return prev;
          if ((prev.messages || []).some(m => String(m.id) === String(newId))) return prev;
          return { ...prev, messages: [...(prev.messages || []), {
            id: newId,
            sender_id: myId,
            sender_name: 'You',
            sender_photo_url: null,
            is_ai: false,
            content: sentContent,
            attachments: sentAttachments,
            is_system: false,
            is_deleted: false,
            created_at: new Date().toISOString(),
            seen_by: [],
            seen_by_count: 0,
          }] };
        });
      }
      // Reconcile from server if realtime is offline.
      setTimeout(() => { if (!realtime.connected) load(true); }, 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const searchAddUsers = useCallback(async (q) => {
    setAddSearching(true);
    try {
      const res = await api.get('/admin/users/search', { params: { q: q || '', limit: 20 } });
      setAddResults(res.data?.results || res.data || []);
    } catch {
      setAddResults([]);
    } finally {
      setAddSearching(false);
    }
  }, []);

  const addMember = async (uid) => {
    setAddingId(uid);
    try {
      await api.post(`/messages/channels/${channelId}/members`, { user_id: uid });
      toast.success('Added to the event chat');
      setShowAdd(false); setAddQ(''); setAddResults([]);
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add this person');
    } finally {
      setAddingId(null);
    }
  };

  const removeMember = async (uid) => {
    if (!window.confirm('Remove this person from the event chat?')) return;
    try {
      await api.delete(`/messages/channels/${channelId}/members/${uid}`);
      toast.success('Removed from the event chat');
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove this person');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;    try {
      await api.delete(`/messages/channels/${channelId}/messages/${messageId}`);
      // ws will fan-out the delete; update locally too in case sender is offline.
      setData(prev => prev ? {
        ...prev,
        messages: (prev.messages || []).map(m => m.id === messageId
          ? { ...m, is_deleted: true, content: '', attachments: [] }
          : m),
      } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Use Cloudinary signed upload (same flow as JobPhotoUpload).
      const sigRes = await api.post('/cloudinary/signature', {
        folder: 'ieventur/messaging',
        resource_type: 'image',
      });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;
      const fd = new FormData();
      fd.append('file', await downscaleImage(file));
      fd.append('signature', signature);
      fd.append('timestamp', timestamp);
      fd.append('api_key', api_key);
      fd.append('folder', 'ieventur/messaging');
      const r = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: fd },
      );
      const j = await r.json();
      if (!j.secure_url) throw new Error('Upload failed');
      setAttachments(prev => [...prev, j.secure_url]);
    } catch (err) {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }
  if (!data) return null;

  const myId = (() => {
    try {
      const t = JSON.parse(atob((localStorage.getItem('token') || '..').split('.')[1] || ''));
      return t?.user_id || t?.sub;
    } catch { return null; }
  })();

  // Iter-177 — channel type styling + pin permissions.
  const ctMeta = CHANNEL_TYPE_META[data.channel_type] || CHANNEL_TYPE_META.general;
  const myMember = (data.members || []).find(m => String(m.user_id) === String(myId));
  const myRole = JSON.parse(atob((localStorage.getItem('token') || '..').split('.')[1] || '{}'))?.role;
  const canPin = ['admin', 'super_admin'].includes(myRole)
              || (myMember && ['client', 'shift_lead'].includes(myMember.role));
  const isAdmin = ['admin', 'super_admin'].includes(myRole);
  const canAlertTeam = isAdmin || (myMember && ['client', 'shift_lead'].includes(myMember.role));
  const isEmergency = data.channel_type === 'emergency';

  return (
    <div className={`min-h-screen flex flex-col ${isEmergency ? 'bg-rose-50/40' : 'bg-gray-50'}`} data-testid="event-channel-page">
      <header className={`bg-white border-b sticky top-0 z-10 ${ctMeta.accent}`}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-base font-semibold truncate" data-testid="channel-title">{data.title}</h1>
              <Badge className={`${ctMeta.tone} text-[10px] uppercase tracking-wide border shrink-0`}
                     data-testid="channel-type-badge">
                {ctMeta.label}
              </Badge>
              {/* Iter-181 — realtime connection status */}
              <span
                className={`shrink-0 inline-flex items-center gap-1 text-[10px] ${wsConnected ? 'text-emerald-600' : 'text-gray-400'}`}
                data-testid="realtime-status"
                title={wsConnected ? 'Live updates connected' : 'Live updates offline — polling'}
              >
                {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {wsConnected ? 'Live' : 'Offline'}
              </span>
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
              {data.venue_name && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {data.venue_name}</span>
              )}
              {data.shift_date && (
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDateMonthDay(data.shift_date)}</span>
              )}
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {data.members?.length || 0} members</span>
            </div>
          </div>
        </div>
        {/* Member rail */}
        <div className="max-w-3xl mx-auto px-4 pb-2 flex items-center gap-2 overflow-x-auto" data-testid="channel-members">
          {(data.members || []).map(m => (
            <div key={m.user_id} className="shrink-0 relative flex flex-col items-center">
              <button
                type="button"
                onClick={() => m.role === 'talent' && openProfile(m.user_id, { surface: 'message_thread' })}
                className="flex flex-col items-center gap-0.5 group"
                data-testid={`channel-member-${m.user_id}`}
                title={m.role}
              >
                <span className="relative">
                  {m.photo_url ? (
                    <img src={m.photo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-gray-200 group-hover:ring-2 group-hover:ring-cyan-300 transition" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-semibold text-cyan-700 group-hover:ring-2 group-hover:ring-cyan-300 transition">
                      {(m.first_name || m.company_name || '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  {onlineUsers[m.user_id] && (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white"
                      data-testid={`presence-online-${m.user_id}`}
                      title="Online"
                    />
                  )}
                </span>
                <span className="text-[10px] text-gray-500 capitalize">{m.role}</span>
              </button>
              {isAdmin && String(m.user_id) !== String(myId) && (
                <button
                  type="button"
                  onClick={() => removeMember(m.user_id)}
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white flex items-center justify-center shadow hover:bg-rose-600"
                  data-testid={`remove-member-${m.user_id}`}
                  title="Remove from chat"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          ))}
          {isAdmin && (
            <button
              type="button"
              onClick={() => { setShowAdd(true); setAddQ(''); setAddResults([]); searchAddUsers(''); }}
              className="shrink-0 flex flex-col items-center gap-0.5 group"
              data-testid="add-member-btn"
              title="Add client or staff"
            >
              <span className="w-10 h-10 rounded-full border-2 border-dashed border-cyan-400 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-50 transition">
                <UserPlus className="w-4 h-4" />
              </span>
              <span className="text-[10px] text-cyan-600">Add</span>
            </button>
          )}
        </div>
      </header>

      {/* Iter-177 — Operational pin tray */}
      <ChannelPinTray
        channelId={channelId}
        pinnedItems={data.pinned_items || []}
        canEdit={canPin}
        onChanged={() => load(false)}
      />

      <div className="flex-1 overflow-y-auto" data-testid="channel-messages">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-3">
          {(data.messages || []).map(m => {
            const isMe = String(m.sender_id) === String(myId);
            const isAI = !!m.is_ai;
            return (
              <div
                key={m.id}
                className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : ''}`}
                data-testid={`channel-message-${m.id}`}
              >
                <button
                  type="button"
                  onClick={() => !isMe && !isAI && openProfile(m.sender_id, { surface: 'message_thread' })}
                  className="shrink-0"
                  data-testid={`message-sender-photo-${m.id}`}
                >
                  {isAI ? (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center"
                         data-testid={`ai-avatar-${m.id}`}>
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  ) : m.sender_photo_url ? (
                    <img src={m.sender_photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-[10px] font-semibold text-cyan-700">
                      {(m.sender_name || '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </button>
                <div className={`max-w-[78%] ${isMe ? 'items-end text-right' : 'items-start'}`}>
                  {!isMe && (
                    <p className="text-[11px] text-gray-500 mb-0.5 px-1 flex items-center gap-1">
                      {isAI && <Sparkles className="w-2.5 h-2.5 text-fuchsia-500" />}
                      {m.sender_name}
                      {isAI && <span className="text-[9px] uppercase tracking-wide text-fuchsia-600">· Generated by AI</span>}
                    </p>
                  )}
                  <div
                    className={`rounded-2xl px-3 py-2 text-sm ${
                      m.is_deleted
                        ? 'bg-gray-100 border border-dashed border-gray-300 text-gray-400 italic'
                        : isAI
                          ? 'bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-fuchsia-200 text-gray-900'
                          : isMe
                            ? 'bg-cyan-500 text-white'
                            : 'bg-white border border-gray-200 text-gray-900'
                    }`}
                    data-testid={isAI ? `ai-message-${m.id}` : (m.is_deleted ? `deleted-message-${m.id}` : undefined)}
                  >
                    {m.is_deleted ? (
                      <p className="text-xs">Message removed{m.deleted_reason ? ` (${m.deleted_reason})` : ''}</p>
                    ) : (
                      <>
                        {m.content && <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                        {(m.attachments || []).length > 0 && (
                          <div className="grid grid-cols-2 gap-1.5 mt-2">
                            {m.attachments.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                                <img src={url} alt="" className="rounded-lg max-h-40 object-cover" loading="lazy" />
                              </a>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className={`flex items-center gap-2 mt-0.5 px-1 ${isMe ? 'justify-end' : ''}`}>
                    <p className="text-[10px] text-gray-400">{formatTime12h(m.created_at)}</p>
                    {/* Iter-177 — Seen by N rail (own messages + AI messages only — privacy on inbound) */}
                    {(isMe || isAI) && m.seen_by_count > 0 && !m.is_deleted && (
                      <p className="text-[10px] text-cyan-600 flex items-center gap-0.5"
                         data-testid={`seen-by-${m.id}`}>
                        <CheckCheck className="w-3 h-3" /> Seen by {m.seen_by_count}
                      </p>
                    )}
                    {/* Iter-181 — moderation/self-delete */}
                    {!m.is_deleted && (isMe || canPin) && (
                      <button
                        type="button"
                        onClick={() => deleteMessage(m.id)}
                        className="text-[10px] text-rose-500 hover:text-rose-700 flex items-center gap-0.5"
                        data-testid={`delete-message-${m.id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {(data.messages || []).length === 0 && (
            <p className="text-center text-sm text-gray-500 py-12" data-testid="channel-empty">
              No messages yet — say hi to your team.
            </p>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <footer className="bg-white border-t border-gray-200 sticky bottom-0">
        {/* Iter-181 — Typing indicator */}
        {Object.keys(typingUsers).length > 0 && (() => {
          const names = Object.keys(typingUsers)
            .map(uid => (data.members || []).find(m => String(m.user_id) === String(uid)))
            .filter(Boolean)
            .map(m => m.first_name || m.company_name || 'Someone')
            .slice(0, 3);
          const label = names.length === 0
            ? 'Someone is typing…'
            : names.length === 1
              ? `${names[0]} is typing…`
              : `${names.join(', ')} are typing…`;
          return (
            <div className="max-w-3xl mx-auto px-4 py-1.5 text-xs text-gray-500 italic flex items-center gap-1" data-testid="typing-indicator">
              <span className="inline-flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
              {label}
            </div>
          );
        })()}
        {canAlertTeam && (
          <div className="max-w-3xl mx-auto px-3 pt-2 flex items-center gap-2" data-testid="urgent-toggle-row">
            <Switch
              id="urgent-switch"
              checked={urgent}
              onCheckedChange={setUrgent}
              data-testid="urgent-toggle"
            />
            <label htmlFor="urgent-switch" className={`text-xs flex items-center gap-1 cursor-pointer ${urgent ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
              <Siren className="w-3.5 h-3.5" /> Urgent — text the whole team now
            </label>
          </div>
        )}
        <div className="max-w-3xl mx-auto px-3 py-3 flex items-end gap-2">
          <input
            type="file"
            ref={fileRef}
            onChange={handleFile}
            accept="image/*"
            className="hidden"
            data-testid="attachment-input"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="attach-image-btn"
            className="shrink-0"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          </Button>
          <div className="flex-1">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachments.map((url, i) => (
                  <div key={i} className="relative">
                    <img src={url} alt="" className="w-12 h-12 rounded object-cover border border-gray-200" />
                    <button
                      type="button"
                      onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white flex items-center justify-center"
                      data-testid={`remove-attachment-${i}`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Textarea
              value={content}
              onChange={(e) => { setContent(e.target.value); sendTypingThrottled(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message..."
              rows={1}
              className="min-h-[40px] resize-none"
              data-testid="channel-message-input"
              id="message-composer-input"
            />
          </div>
          <Button
            onClick={send}
            disabled={sending || (!content.trim() && attachments.length === 0)}
            className="bg-cyan-500 hover:bg-cyan-600 text-white shrink-0"
            data-testid="channel-send-btn"
            id="message-composer-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </footer>

      {/* Admin: add the client or specific talent/staff to this event chat */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle>Add to event chat</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              autoFocus
              value={addQ}
              onChange={(e) => { setAddQ(e.target.value); searchAddUsers(e.target.value); }}
              placeholder="Search by name, email or phone…"
              data-testid="add-member-search"
            />
            <div className="max-h-72 overflow-y-auto divide-y divide-gray-100" data-testid="add-member-results">
              {addSearching && (
                <div className="py-6 flex justify-center text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /></div>
              )}
              {!addSearching && addResults.length === 0 && (
                <div className="py-6 text-center text-sm text-gray-400" data-testid="add-member-empty">No people found</div>
              )}
              {!addSearching && addResults.map(u => (
                <div key={u.id} className="flex items-center gap-3 py-2" data-testid={`add-member-row-${u.id}`}>
                  {u.profile_photo ? (
                    <img src={u.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover border border-gray-200" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-semibold text-cyan-700">
                      {(u.full_name || u.email || '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{u.full_name || u.email}</div>
                    <div className="text-xs text-gray-500 capitalize truncate">{u.role}{u.email ? ` · ${u.email}` : ''}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => addMember(u.id)}
                    disabled={addingId === u.id}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white shrink-0"
                    data-testid={`add-member-confirm-${u.id}`}
                  >
                    {addingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventChannelDetail;
